<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useConfigStore } from '@toni/rendering/src/stores/config'
import { BlockContainer } from '@toni/brand-ui'
import { usePageStore } from '@toni/rendering/src/stores/page'
import BlockExternalHeaderBanner from '@toni/rendering-nxt/src/components/content-blocks/BlockExternalHeaderBanner.vue'

import { useImageProps } from '../../../../../components/bauer-image/use-image-props'
import { isPortraitImage, createImgSrc, getImgDefinitions, createImgSrcSetArray, setMediaQuery } from '../../../../../components/bauer-image/image-utils'
import { BlockArticleMeta } from '../../../../../components/ui/article-meta'
import {
  ArticleHeader,
  Badge,
  Image,
  Figure,
  Picture,
  Figcaption,
  InfoBox,
  PictureWithBlur,
  InfoBoxContent
} from '../../../../../components/ui/article-header'
import { PageBody } from '../../../../../utils/page-body-schema'
import { MetaData } from '../../../../../utils/meta-data-schema'
import { toValidHTML } from '../../../../../utils/html-utils'
import VideoPlayer from '../../../../../components/content-blocks/video/VideoPlayer.vue'
import MediaGallery from '../../../../../components/content-blocks/gallery/MediaGallery.vue'

import { Diagonal } from '.'

import type { BauerImage } from '../../../../../components/bauer-image/bauer-image-types'

const { t } = useI18n()

const props = defineProps<{
  body?: PageBody
  metaData?: MetaData
}>()

const pageStore = usePageStore()
const brand = usePageStore().brandFromStore as string
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
const setName = 'article-header' as string
const imageDefinitions = getImgDefinitions(brand, imageSrcDefinitions, setName)
const imgFallbackWidth = imageDefinitions.maxWidth ?? imageSrcDefinitions?.maxWidth ?? 0
const imgFallbackHeight = computed(() => {
  const width = image?.value?.width || 0
  const height = image?.value?.height || 0
  const ratio = height / width
  return Math.round((imgFallbackWidth * ratio))
})

const adaAutoNative = computed(() => pageStore.pageData?.metaData?.adaAutoNative as boolean)
const isHome = computed(() => Boolean(pageStore.isHome))
const size = computed(() => {
  return isHome.value ? 'full' : 'normal'
})
const isWide = computed(() => !isHome.value && pageStore.pageData?.pageMeta?.pageType === 'article')

const composedAuthors = computed(() => {
  const authors = [props?.body?.header?.author]
  if (props?.body?.header?.additionalAuthors) {
    authors.push(...props.body.header.additionalAuthors)
  }

  return (authors && authors.filter(a => !!a).length > 0) ? authors : undefined
})

const image = computed(() => props.body?.header?.image)
const video = computed(() => props.body?.header?.video)
const slider = computed(() => props.body?.header?.slider?.galleryItems ?? [])

const isPortrait = computed(() => {
  if (!image.value) return false
  return isPortraitImage(image.value.width || 0, image.value.height || 0)
})

const hasCropping = computed(() => {
  if (!image.value) return false
  return image.value.crop && Object.keys(image.value.crop).length > 0
})

const imgProps = useImageProps({
  image: image.value,
  brand,
  imageServiceUrl,
  setName,
  srcSetDefinition: {
    format: 'landscape'
  }
}, imgFallbackWidth, imgFallbackHeight.value)

const imgSrcSetArray = (image: BauerImage) => {
  return createImgSrcSetArray({
    image,
    brand,
    imageServiceUrl,
    setName,
    imageSrcDefinitions
  })
}

const blurredBgSrc = () => {
  if (!image.value?.imageId && !image.value?.externalImageSrc) return undefined
  return (createImgSrc({
    image: props.body?.header?.image,
    brand,
    imageServiceUrl,
    setName: 'image-m',
    srcSetDefinition: {
      format: 'landscape'
    }
  }, imgFallbackWidth ?? 0))?.href ?? ''
}

</script>

<template>
  <ArticleHeader data-testid="article-header">
    <BlockExternalHeaderBanner v-if="!!body?.externalHeaderBanner"
                               :image="body.externalHeaderBanner"
                               :page-type="'article'" />
    <BlockContainer class="gap-4 md:gap-3"
                    :size="isWide ? 'wide' : size">
      <Badge v-if="!!body?.header?.roofline"
             :type="'text'"
             :variant="'primary'"
             :size="'lg'"
             :data-ems="adaAutoNative ? 'ems-native-subheadline' : null"
             class="font-badge text-badge-lg normal-case py-3 px-4 md:px-2 gap-[10px]">
        {{ body.header.roofline }}
      </Badge>
      <h1 v-if="!!body?.header?.headline"
          :data-ems="adaAutoNative ? 'ems-native-headline' : null"
          class="font-heading text-heading-lg md:text-heading-3xl mb-4 md:mb-3">
        {{ body.header.headline }}
      </h1>
    </BlockContainer>
    <BlockContainer v-if="body?.header?.introductionText"
                    :size="isWide ? 'wide' : size">
      <div :data-ems="adaAutoNative ? 'ems-native-headlinetext' : null"
           class="blockText prose pb-6 md:pb-10 font-body text-foreground text-body-md md:text-body-lg max-w-full prose-a:text-secondary-500 hover:prose-a:text-primary-350 prose-a:decoration-from-font prose-a:underline-offset-2 prose-li:m-0 prose-li:leading-7 prose-li:ps-0 sm:prose-li:leading-8 prose-li:marker:text-foreground [&_u]:decoration-from-font [&_u]:underline-offset-2 [&_li_p]:m-0"
           v-html="toValidHTML(body?.header?.introductionText || '')" />
    </BlockContainer>
    <div v-if="!!image"
         class="w-full">
      <Image :class="adaAutoNative ? 'mb-6 md:mb-8' : ''">
        <Figure>
          <Picture
            :class="isPortrait
              ? 'absolute top-0 flex justify-center items-center w-full aspect-4/3 md:aspect-video z-10'
              : 'relative w-full aspect-4/3 md:aspect-video'">
            <template v-if="!adaAutoNative && !image?.externalImageSrc">
              <source v-for="(singleSrc, index) in imgSrcSetArray(image)"
                      :key="index + 'webp'"
                      type="image/webp"
                      :media="setMediaQuery(singleSrc.viewportWidth)"
                      :srcset="singleSrc.src">
            </template>
            <img v-bind="imgProps"
                 :class="isPortrait ? ['h-full max-h-full max-w-full', hasCropping ? 'object-cover' : 'object-contain'] : 'w-full h-full object-cover rounded-image'"
                 :data-ems="adaAutoNative ? 'ems-native-mainimage' : null">
          </Picture>
          <PictureWithBlur v-if="isPortrait">
            <img :src="blurredBgSrc() ?? ''"
                 alt=""
                 class="w-full h-full object-cover filter blur-md scale-110">
          </PictureWithBlur>
        </Figure>
        <div class="relative z-20">
          <div class="absolute bottom-[calc(100%-1px)] left-0 w-full leading-none text-accent-primary-background">
            <Diagonal
              class="w-full h-full"
              :width="764"
              :height="30"
              :start-height="30" />
          </div>
          <Figcaption class="flex flex-wrap gap-x-1 items-center bg-accent-primary-background px-4 py-2 md:py-3 md:px-5 pt-2 sm:pt-0 leading-none">
            <p v-if="image?.imageDescription?.plain"
               class="font-ui text-ui-sm text-text-baseInverted"
               :data-ems="adaAutoNative ? 'ems-native-imagetext' : null">
              {{ image?.imageDescription.plain }}
            </p>
            <p v-if="image?.imageCredit"
               class="font-ui text-ui-sm text-text-baseInverted"
               :data-ems="adaAutoNative ? 'ems-native-copyright' : null">
              © {{ image?.imageCredit }}
            </p>
          </Figcaption>
        </div>
      </Image>
    </div>
    <div v-if="!!video"
         class="w-full">
      <VideoPlayer :video="video"
                   class="w-full" />
    </div>
    <div v-if="slider?.length"
         class="w-full">
      <MediaGallery :gallery-items="slider" />
    </div>
    <BlockContainer v-if="!!composedAuthors || body?.header?.displayDate || metaData?.showAffiliateDisclaimer"
                    class="my-6 md:mt-10">
      <BlockArticleMeta v-if="!!composedAuthors || (body?.header?.displayDate && !metaData?.hideDisplayDate)"
                        class="border-b-separator-width pb-3 mb-4"
                        :composed-authors="composedAuthors"
                        :brand="brand"
                        :display-date="!metaData?.hideDisplayDate ? body?.header?.displayDate : undefined" />
      <InfoBox v-if="metaData?.showAffiliateDisclaimer">
        <InfoBoxContent class="text-center">
          <p class="font-ui text-ui-md">
            {{ t('articleHeader.affiliateDisclaimer') }}
          </p>
        </InfoBoxContent>
      </InfoBox>
    </BlockContainer>
  </ArticleHeader>
</template>
